import * as React from "react";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { Ordering } from "@/types/base";
import { ChevronDown, ChevronUp, Minus, Pencil, Plus } from "lucide-react";
import { Tooltip } from "@/components/ui/tooltip";
import {
  useCreateNoteColumnMutation,
  useRenameNoteColumnMutation,
  useDeleteNoteColumnMutation,
} from "@/modules/snapshots/businessReports.api";
import { SnapshotCollection } from "@/modules/snapshots/snapshotCollection.types";
import { handleNoteError } from "@/modules/snapshots/components/noteErrors";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SortableContext, horizontalListSortingStrategy } from "@dnd-kit/sortable";
import { DraggableColumnHeader } from "./draggableColumnHeader";
import { ConfirmDeleteNoteColumnDialog } from "./confirmDeleteNoteColumnDialog";
import { ColumnActionsMenu } from "./columnActionsMenu";
import { ColumnReviewProgressChip } from "./columnReviewProgressChip";
import { ColumnFilterPopover } from "@/modules/snapshots/grid/columnFilterPopover";
import {
  getNextSnapshotGridOrdering,
  type ColumnFilterValue,
  type ColumnFilters,
  type ColumnKind,
  type ColumnReviewInfo,
} from "@/modules/snapshots/grid/sortAndFilter";
import {
  SNAPSHOT_GRID_ADD_NOTE_COLUMN_WIDTH,
  getSnapshotGridColumnStyle,
  getSnapshotGridColumnWidth,
  type SnapshotGridColumnWidths,
} from "./layout";
import type { SnapshotGridField } from "@/modules/snapshots/businessReports.api";

interface Props {
  setOrdering: (ordering: Ordering) => void;
  ordering: Ordering;
  headers: string[];
  headerPrompts?: Record<string, string | null>;
  noteHeaders: string[];
  onColumnAnalysisClick?: (header: string) => void;
  onReviewColumnClick?: (header: string) => void;
  columnReview?: Record<string, ColumnReviewInfo>;
  onRerunColumnClick?: (header: string) => void;
  rerunnableColumns?: ReadonlySet<string>;
  snapshotCollection: SnapshotCollection;
  businessReportId?: string;
  columnFilters?: ColumnFilters;
  columnTypes?: Record<string, ColumnKind>;
  onColumnFilterChange?: (columnName: string, value: ColumnFilterValue | undefined) => void;
  onNoteColumnDeleted?: (columnName: string) => void;
  columnWidths?: SnapshotGridColumnWidths;
  onColumnResize?: (columnId: string, width: number) => void;
  dataColumns?: SnapshotGridField[];
}

type DialogMode = { type: "create" } | { type: "rename"; oldName: string };

interface ColumnFormData {
  name: string;
}

interface SortableHeaderLabelProps {
  label: string;
  ordering: Ordering;
  onSort: (field: string) => void;
}

const SortableHeaderLabel = React.forwardRef<
  HTMLSpanElement,
  SortableHeaderLabelProps & React.HTMLAttributes<HTMLSpanElement>
>(({ label, ordering, onSort, ...rest }, ref) => {
  const isActive = ordering.order_by === label;
  return (
    <span
      ref={ref}
      {...rest}
      className="flex min-w-0 cursor-pointer items-center"
      onClick={() => onSort(label)}
      onKeyDown={e => e.key === "Enter" && onSort(label)}
      role="button"
      tabIndex={0}
    >
      <span className="truncate">{label}</span>
      {isActive && ordering.descending ? (
        <ChevronDown className="ml-2 h-4 w-4 flex-shrink-0" />
      ) : (
        <ChevronUp className={`ml-2 h-4 w-4 flex-shrink-0 ${isActive ? "" : "text-gray-400"}`} />
      )}
    </span>
  );
});
SortableHeaderLabel.displayName = "SortableHeaderLabel";

export const SnapshotGridTableScrollableHeader: React.FC<Props> = ({
  setOrdering,
  ordering,
  headers,
  headerPrompts,
  noteHeaders,
  onColumnAnalysisClick,
  onReviewColumnClick,
  columnReview,
  onRerunColumnClick,
  rerunnableColumns,
  snapshotCollection,
  businessReportId,
  columnFilters,
  columnTypes,
  onColumnFilterChange,
  onNoteColumnDeleted,
  columnWidths,
  onColumnResize,
  dataColumns,
}) => {
  const [dialogMode, setDialogMode] = React.useState<DialogMode | null>(null);
  const [pendingDelete, setPendingDelete] = React.useState<string | null>(null);
  const [createColumn] = useCreateNoteColumnMutation();
  const [renameColumn] = useRenameNoteColumnMutation();
  const [deleteColumn] = useDeleteNoteColumnMutation();

  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<ColumnFormData>();

  const handleClick = (field: string) => {
    setOrdering(getNextSnapshotGridOrdering(ordering, field));
  };

  const handleOpenCreateDialog = () => {
    if (!businessReportId) return;
    const idx = noteHeaders.length + 1;
    setDialogMode({ type: "create" });
    reset({ name: `Notes ${idx}` });
  };

  const handleOpenRenameDialog = (header: string) => {
    if (!businessReportId) return;
    setDialogMode({ type: "rename", oldName: header });
    reset({ name: header });
  };

  const handleCloseDialog = () => {
    setDialogMode(null);
  };

  const onSubmit = async (data: ColumnFormData) => {
    if (!businessReportId || !dialogMode) return;
    const trimmed = data.name.trim();
    if (!trimmed) {
      handleCloseDialog();
      return;
    }

    try {
      if (dialogMode.type === "create") {
        await createColumn({
          snapshotCollectionId: snapshotCollection.id,
          businessReportId,
          name: trimmed,
        }).unwrap();
      } else {
        if (trimmed === dialogMode.oldName) {
          handleCloseDialog();
          return;
        }
        const result = await renameColumn({
          snapshotCollectionId: snapshotCollection.id,
          businessReportId,
          oldName: dialogMode.oldName,
          newName: trimmed,
        }).unwrap();
        if (result.renamed === 0) {
          toast.warning("No notes were found to rename");
        }
      }
      handleCloseDialog();
    } catch (error) {
      handleNoteError(error, dialogMode.type === "create" ? "Failed to create column" : "Failed to rename column");
    }
  };

  const handleDeleteColumn = async (columnName: string) => {
    if (!businessReportId) return;
    try {
      await deleteColumn({
        snapshotCollectionId: snapshotCollection.id,
        businessReportId,
        columnName,
      }).unwrap();
    } catch (error) {
      handleNoteError(error, "Failed to delete column");
      return;
    }
    onNoteColumnDeleted?.(columnName);
  };

  const canEdit = !!businessReportId;

  React.useEffect(() => {
    if (pendingDelete !== null && !noteHeaders.includes(pendingDelete)) {
      setPendingDelete(null);
    }
  }, [noteHeaders, pendingDelete]);

  const noteHeaderIds = noteHeaders.map(h => `note:${h}`);

  const renderPromptHeader = (header: string, columnId = header, readOnly = false) => {
    const prompt = headerPrompts?.[header];
    const reviewInfo = columnReview?.[header];
    const persistedFilter = columnFilters?.[header];
    const showReviewFilterSection = !!reviewInfo?.summary || (persistedFilter?.reviewVerdicts?.length ?? 0) > 0;
    const canReview = !!onReviewColumnClick && !!reviewInfo;
    const canRerun = !!onRerunColumnClick && !!rerunnableColumns?.has(header);
    const showKebab = !readOnly && (!!onColumnAnalysisClick || canReview || canRerun);
    return (
      <DraggableColumnHeader
        key={columnId}
        columnId={columnId}
        width={getSnapshotGridColumnWidth(columnId, columnWidths)}
        onResize={onColumnResize}
      >
        <Tooltip
          text={
            <div className="max-w-xs">
              <p className="font-semibold">{header}</p>
              {prompt && <p className="mt-2 text-xs font-normal text-gray-300">{prompt}</p>}
            </div>
          }
          className="rounded bg-black text-xs text-white"
          side="bottom"
          delayDuration={300}
        >
          <SortableHeaderLabel label={header} ordering={ordering} onSort={handleClick} />
        </Tooltip>
        <div className="ml-auto flex shrink-0 items-center gap-1">
          {reviewInfo?.summary?.status === "IN_PROGRESS" && (
            <ColumnReviewProgressChip header={header} summary={reviewInfo.summary} />
          )}
          {onColumnFilterChange && (
            <ColumnFilterPopover
              columnName={header}
              columnKind={columnTypes?.[header] ?? "text"}
              value={columnFilters?.[header]}
              onChange={onColumnFilterChange}
              showReviewSection={showReviewFilterSection}
            />
          )}
          {showKebab ? (
            <ColumnActionsMenu
              header={header}
              onAnalyseClick={onColumnAnalysisClick ? () => onColumnAnalysisClick(header) : undefined}
              onReviewClick={canReview && reviewInfo.eligible ? () => onReviewColumnClick(header) : undefined}
              reviewDisabledReason={
                canReview && !reviewInfo.eligible
                  ? "Review is not available for image or web-search columns"
                  : undefined
              }
              onRerunClick={canRerun ? () => onRerunColumnClick(header) : undefined}
            />
          ) : null}
        </div>
      </DraggableColumnHeader>
    );
  };

  const renderNoteHeader = (noteHeader: string, columnId: string, isLast: boolean) => (
    <DraggableColumnHeader
      key={columnId}
      columnId={columnId}
      width={getSnapshotGridColumnWidth(columnId, columnWidths)}
      onResize={onColumnResize}
    >
      <Tooltip text={noteHeader} className="rounded bg-black text-xs text-white" side="bottom" delayDuration={300}>
        <SortableHeaderLabel label={noteHeader} ordering={ordering} onSort={handleClick} />
      </Tooltip>
      {onColumnFilterChange && (
        <ColumnFilterPopover
          columnName={noteHeader}
          columnKind="notes"
          value={columnFilters?.[noteHeader]}
          onChange={onColumnFilterChange}
        />
      )}
      <div className="ml-auto flex shrink-0 items-center gap-1">
        {canEdit && (
          <Tooltip text="Rename note column" className="rounded bg-black text-xs text-white">
            <button
              type="button"
              onClick={() => handleOpenRenameDialog(noteHeader)}
              className="rounded p-0.5 text-gray-400 opacity-0 hover:bg-gray-200 hover:text-gray-600 group-hover/header:opacity-100"
            >
              <Pencil className="h-4 w-4" />
            </button>
          </Tooltip>
        )}
        {canEdit && (
          <Tooltip text="Delete note column" className="rounded bg-black text-xs text-white">
            <button
              type="button"
              aria-label={`Delete note column ${noteHeader}`}
              onClick={() => setPendingDelete(noteHeader)}
              className="rounded p-0.5 text-gray-400 opacity-0 hover:bg-red-100 hover:text-red-600 focus-visible:opacity-100 group-hover/header:opacity-100"
            >
              <Minus className="h-4 w-4" />
            </button>
          </Tooltip>
        )}
        {isLast && canEdit && (
          <Tooltip text="Add note column" className="rounded bg-black text-xs text-white">
            <button
              type="button"
              onClick={handleOpenCreateDialog}
              className="rounded p-0.5 text-gray-400 opacity-0 hover:bg-gray-200 hover:text-gray-600 group-hover/header:opacity-100"
            >
              <Plus className="h-4 w-4" />
            </button>
          </Tooltip>
        )}
      </div>
    </DraggableColumnHeader>
  );

  return (
    <>
      {dataColumns ? (
        <SortableContext items={dataColumns.map(field => field.key)} strategy={horizontalListSortingStrategy}>
          {dataColumns.map((field, index) =>
            field.is_note
              ? renderNoteHeader(
                  field.name,
                  field.key,
                  !dataColumns.slice(index + 1).some(candidate => candidate.is_note),
                )
              : renderPromptHeader(field.name, field.key, field.read_only),
          )}
        </SortableContext>
      ) : (
        <>
          <SortableContext items={headers} strategy={horizontalListSortingStrategy}>
            {headers.map(header => renderPromptHeader(header))}
          </SortableContext>
          <SortableContext items={noteHeaderIds} strategy={horizontalListSortingStrategy}>
            {noteHeaders.map((noteHeader, index) =>
              renderNoteHeader(noteHeader, `note:${noteHeader}`, index === noteHeaders.length - 1),
            )}
          </SortableContext>
        </>
      )}
      {noteHeaders.length === 0 && canEdit && (
        <th
          scope="col"
          className="sticky top-0 z-30 overflow-hidden bg-[#f1edea] px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
          style={getSnapshotGridColumnStyle(SNAPSHOT_GRID_ADD_NOTE_COLUMN_WIDTH)}
        >
          <Tooltip text="Add note column" className="rounded bg-black text-xs text-white">
            <button
              type="button"
              onClick={handleOpenCreateDialog}
              className="rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-600"
            >
              <Plus className="h-4 w-4" />
            </button>
          </Tooltip>
        </th>
      )}

      <Dialog open={!!dialogMode} onOpenChange={open => !open && handleCloseDialog()}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{dialogMode?.type === "create" ? "Add note column" : "Rename note column"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit(onSubmit)}>
            <input
              {...register("name", { required: true })}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-coral focus:outline-none focus:ring-1 focus:ring-coral"
              autoFocus
            />
            <DialogFooter className="mt-4">
              <Button type="button" variant="outline" size="sm" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={isSubmitting}>
                {dialogMode?.type === "create" ? "Create" : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {pendingDelete !== null && (
        <ConfirmDeleteNoteColumnDialog
          isOpen={pendingDelete !== null}
          setIsOpen={() => setPendingDelete(null)}
          columnName={pendingDelete}
          confirmCb={() => {
            const columnName = pendingDelete;
            setPendingDelete(null);
            void handleDeleteColumn(columnName);
          }}
        />
      )}
    </>
  );
};
